/** Property Expenses (v1 expenses-calendar-materials.js expensesView). The EXP2
 * org-settings ledger: v1 tuple rows [date,desc,cat,amount,pay,status,vendor,...].
 * Add / delete write the WHOLE array through the settings door (PHASE19), gated by
 * liveFire `expensesEdit`. Per-deal expenses live on the deal drawer; this is the
 * cross-property ledger. */
import { useEffect, useMemo, useState } from "react";
import { liveOn } from "../../lib/liveFire";
import { natKey } from "../../lib/natkey";
import { todayIsoAppTz } from "../../lib/apptime";
import {
  EXP_CATS, EXP_STATUS, LEX_PAY, LEX_STATUS, EXPENSE_MSG,
  allPropExpensesRaw, narrowPropExpense, propExpenseToRaw,
  allTxnExpensesRaw, narrowTxnExpense, dealExpenseAsLedgerRow,
  buildExpenseCsv, expenseId, isoToDisplay, fmtMoneyExact, expenseBlocker,
  type PropExpense,
} from "./api";

const money = fmtMoneyExact;

function downloadCsv(name: string, csv: string): void {
  try {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch { /* non-DOM env */ }
}

export function ExpensesPage({
  orgSettings,
  onSave,
  onSaveTxn,
  prefill = null,
  onPrefillConsumed,
  dealAddresses = [],
  settingsReady = true,
}: {
  orgSettings: Record<string, unknown>;
  /** returns whether the write was accepted; `false` keeps the form open
   * with the reason (`void` from older callers = accepted) */
  onSave: (all: unknown[][]) => boolean | void;
  /** org settings have actually been READ. Until then an empty ledger means
   *  "unknown", not "you have none" — and saving would replace the real one. */
  settingsReady?: boolean;
  /** whole-array TXNEXP save — enables delete on rows logged from a deal's Expenses tab */
  onSaveTxn?: (all: Record<string, unknown>[]) => void;
  /** Utilities page "Log Expense" jump — opens the form pre-filled. */
  prefill?: { desc: string; category: string; property?: string } | null;
  onPrefillConsumed?: () => void;
  /** property/deal addresses for the "Property" attribution autocomplete */
  dealAddresses?: string[];
}) {
  const canEdit = liveOn("expensesEdit");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [propF, setPropF] = useState(""); // per-property filter ("" = all, "__none__" = unattributed)
  const [adding, setAdding] = useState(false);
  const [formInit, setFormInit] = useState<{ desc: string; category: string; property?: string } | null>(null);

  // Utilities → Log Expense: open the form pre-filled, then clear the request.
  useEffect(() => {
    if (!prefill) return;
    if (canEdit) {
      setFormInit(prefill);
      setAdding(true);
    }
    onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Each row WITH its index in its OWN source array (delete without reindex
  // surprises). Two sources merge here: the EXP2 cross-property ledger ("prop")
  // and per-deal TXNEXP rows logged on a deal's Expenses tab ("deal") — so an
  // expense logged on e.g. 7015 N Palmetto shows up on this page too.
  const entries = useMemo(() => {
    const prop = allPropExpensesRaw(orgSettings).map((t, idx) => ({ e: narrowPropExpense(t), idx, src: "prop" as const }));
    const deal = allTxnExpensesRaw(orgSettings).map((t, idx) => ({ e: dealExpenseAsLedgerRow(narrowTxnExpense(t), idx), idx, src: "deal" as const }));
    // Newest first; rows without a parseable date sink to the bottom.
    return [...prop, ...deal].sort((a, b) => (b.e.dateIso || "").localeCompare(a.e.dateIso || ""));
  }, [orgSettings]);

  // Property filter options — unique across BOTH ledgers, keyed by natKey so
  // formatting variants of the same address collapse into one entry.
  const propOpts = useMemo(() => {
    const m = new Map<string, string>();
    let blanks = 0;
    for (const { e } of entries) {
      const p = e.property.trim();
      if (!p) { blanks++; continue; }
      const k = natKey(p);
      if (!m.has(k)) m.set(k, p.split(",")[0] ?? p);
    }
    return { opts: [...m.entries()].sort((a, b) => a[1].localeCompare(b[1])), blanks };
  }, [entries]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return entries.filter(({ e }) => {
      if (propF === "__none__" && e.property.trim()) return false;
      if (propF && propF !== "__none__" && natKey(e.property) !== propF) return false;
      if (cat && e.cat !== cat) return false;
      if (status && e.status.toUpperCase() !== status) return false;
      if (!needle) return true;
      return (e.desc + " " + e.vendor + " " + e.cat + " " + e.property).toLowerCase().includes(needle);
    });
  }, [entries, q, cat, status, propF]);

  // Tiles follow the ACTIVE filters — pick a property and Total/This month/
  // Entries become that property's numbers (Tom 2026-07-24).
  const now = new Date();
  const totals = useMemo(() => {
    let total = 0, month = 0;
    for (const { e } of shown) {
      total += e.amount;
      const d = e.dateIso ? new Date(e.dateIso + "T00:00:00") : null;
      if (d && !Number.isNaN(d.getTime()) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) month += e.amount;
    }
    return { total, month, count: shown.length };
  }, [shown, now]);

  const [saveErr, setSaveErr] = useState<string | null>(null);
  const save = (mutate: (all: unknown[][]) => void): boolean => {
    const all = allPropExpensesRaw(orgSettings).map((t) => [...t]);
    mutate(all);
    const ok = onSave(all) !== false;
    setSaveErr(ok ? null : EXPENSE_MSG.notLoaded);
    return ok;
  };
  // a refused save keeps the form (and what was typed) on screen
  const add = (e: PropExpense) => { if (save((all) => { all.unshift(propExpenseToRaw(e)); })) { setAdding(false); setFormInit(null); } };
  const remove = (src: "prop" | "deal", idx: number) => {
    if (src === "prop") { save((all) => { if (idx >= 0 && idx < all.length) all.splice(idx, 1); }); return; }
    if (!onSaveTxn) return;
    const all = allTxnExpensesRaw(orgSettings).map((t) => ({ ...t }));
    if (idx >= 0 && idx < all.length) { all.splice(idx, 1); onSaveTxn(all); }
  };

  return (
    <div className="leads exppage">
      <div className="exphead">
        <h1>Property Expenses</h1>
        <div className="expacts">
          <button className="btn slim" onClick={() => downloadCsv("property-expenses.csv", buildExpenseCsv(
            ["Date", "Invoice #", "Description", "Property", "Category", "Vendor", "Payment", "Amount", "Tax", "Status"],
            shown.map(({ e }) => [e.date || isoToDisplay(e.dateIso), e.invoice, e.desc, e.property, e.cat, e.vendor, e.pay, e.amount, e.tax, e.status]),
          ))}>⤓ Export</button>
          {canEdit ? <button className="btn slim primary" onClick={() => setAdding(true)}>+ Log expense</button> : null}
        </div>
      </div>

      <div className="dkpis exptiles">
        <div className="dkpi expkpi"><span className="expico blue" aria-hidden="true">💲</span><div className="dkpimeta"><span className="lbl">Total</span><strong>{money(totals.total)}</strong></div></div>
        <div className="dkpi expkpi"><span className="expico violet" aria-hidden="true">📅</span><div className="dkpimeta"><span className="lbl">This month</span><strong>{money(totals.month)}</strong></div></div>
        <div className="dkpi expkpi"><span className="expico emerald" aria-hidden="true">📈</span><div className="dkpimeta"><span className="lbl">Entries</span><strong>{totals.count}</strong></div></div>
      </div>

      <div className="bar">
        <input placeholder="Search description, property, vendor, category…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={propF} onChange={(e) => setPropF(e.target.value)} title="Filter by property">
          <option value="">All properties</option>
          {propOpts.blanks ? <option value="__none__">— No property —</option> : null}
          {propOpts.opts.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All categories</option>
          {EXP_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {EXP_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="count">{shown.length} of {entries.length}</span>
      </div>

      {saveErr ? <div className="expsaveerr" role="alert">⚠ {saveErr}</div> : null}
      {adding ? <LogExpenseForm key={formInit ? "pf:" + formInit.desc : "new"} initial={formInit} dealAddresses={dealAddresses} onCancel={() => { setAdding(false); setFormInit(null); setSaveErr(null); }} onSubmit={add} /> : null}

      <div className="list explist">
        <table className="exptable">
          <thead>
            <tr><th>Date</th><th>Description</th><th>Property</th><th>Category</th><th>Vendor</th><th>Payment</th><th className="r">Amount</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {shown.length ? shown.map(({ e, idx, src }) => (
              <tr key={src + ":" + idx}>
                <td className="dim">{e.date || isoToDisplay(e.dateIso) || "—"}</td>
                <td>{e.desc || "—"}</td>
                <td>
                  {e.property ? <span className="expprop">{e.property.split(",")[0]}</span> : <span className="dim">—</span>}
                  {src === "deal" ? <span className="expsrc" title="Logged on the deal's Expenses tab">deal</span> : null}
                </td>
                <td><span className="expcat">{e.cat}</span></td>
                <td>{e.vendor || "—"}</td>
                <td className="dim">{e.pay || "—"}</td>
                <td className="r"><strong>{money(e.amount)}</strong></td>
                <td><span className={"statuschip s-" + e.status.toLowerCase()}>{e.status}</span></td>
                <td className="r">{canEdit && (src === "prop" || onSaveTxn) ? <button className="rowbtn del" title="Delete" onClick={() => remove(src, idx)}>✕</button> : null}</td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="expempty">{
                !settingsReady
                  ? "Loading your expenses… this is not an empty ledger — don't add anything until it appears."
                  : canEdit ? "No property expenses yet — log one." : EXPENSE_MSG.none
              }</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogExpenseForm({ initial = null, dealAddresses = [], onCancel, onSubmit }: { initial?: { desc: string; category: string; property?: string } | null; dealAddresses?: string[]; onCancel: () => void; onSubmit: (e: PropExpense) => void }) {
  // Eastern, not UTC: toISOString() rolls over at 8pm ET, so an expense logged
  // in the evening pre-filled (and saved, and pushed to QuickBooks) as tomorrow.
  const today = todayIsoAppTz();
  const [dateIso, setDateIso] = useState(today);
  const [desc, setDesc] = useState(initial?.desc ?? "");
  // Seed the property from the prefill. Utilities → "Log Expense" knows exactly
  // which property it came from, but only passed it inside the DESCRIPTION
  // text — so the row was saved unattributed and silently excluded from that
  // property's totals, which is the number the button exists to move.
  const [property, setProperty] = useState(initial?.property ?? "");
  const [invoice, setInvoice] = useState("");
  const [cat, setCat] = useState(initial && EXP_CATS.includes(initial.category) ? initial.category : "");
  const [amount, setAmount] = useState("");
  const [tax, setTax] = useState("");
  const [pay, setPay] = useState("None / N/A");
  const [status, setStatus] = useState("Pending");
  const [vendor, setVendor] = useState("");
  const [receipt, setReceipt] = useState("");
  const a = Number(amount.replace(/[^0-9.]/g, ""));
  const tx = Number(tax.replace(/[^0-9.]/g, ""));
  const sub = Number.isFinite(a) ? a : 0;
  const taxN = Number.isFinite(tx) ? tx : 0;
  const money2 = (n: number): string => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // never a silently-inert button: incomplete = dimmed, and the click says why
  const blocker = expenseBlocker({ desc, amount });
  const ok = blocker === null;
  const [why, setWhy] = useState<string | null>(null);
  // RECEIPT UPLOAD IS NOT BUILT. setReceipt(f.name) was the only thing this
  // ever did — the file was never read, never uploaded, and `receipt` is not in
  // the submit payload, there is no storage bucket and no API field. So it said
  // "invoice.pdf — attached" and stored nothing. Finding that out at tax time
  // is the worst possible moment, so the control now says what is true instead
  // of picking a file it will silently discard.
  const pickReceipt = () => { /* intentionally inert — see above */ };
  const submit = () => {
    if (!ok) { setWhy(blocker); return; }
    setWhy(null);
    onSubmit({
      date: isoToDisplay(dateIso), desc: desc.trim(), cat: cat || "Other", amount: sub, tax: taxN,
      pay, status, vendor: vendor.trim(), invoice: invoice.trim(), id: expenseId(Date.now()), dateIso,
      property: property.trim(),
    });
  };
  return (
    <div className="expform">
      <div className="expformtitle">💲 Log property expense</div>
      <button type="button" className="expreceipt" onClick={pickReceipt} disabled
              title="Receipt storage isn't built yet — keep the file with your records">
        <span className="expreceipticon" aria-hidden="true">⬆</span>
        <span className="expreceiptmain">Receipt upload — not available yet</span>
        <span className="expreceiptsub">Keep the file with your own records for now</span>
      </button>
      <div className="expformgrid">
        <label className="wide">Description<input value={desc} placeholder="What was this expense for?" onChange={(e) => setDesc(e.target.value)} /></label>
        <label className="wide">Property <span className="lblhint">(optional — ties this to a property/deal)</span>
          <input list="expprops" value={property} placeholder="Start typing an address…" onChange={(e) => setProperty(e.target.value)} />
          <datalist id="expprops">{dealAddresses.map((a) => <option key={a} value={a} />)}</datalist>
        </label>
        <label>Date<input type="date" value={dateIso} onChange={(e) => setDateIso(e.target.value)} /></label>
        <label>Invoice #<input value={invoice} placeholder="INV-0001" onChange={(e) => setInvoice(e.target.value)} /></label>
        <label>Category<select value={cat} onChange={(e) => setCat(e.target.value)}><option value="">Select category…</option>{EXP_CATS.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label>Amount ($)<input inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} /></label>
        <label>Tax Amount ($)<input inputMode="decimal" value={tax} placeholder="0" onChange={(e) => setTax(e.target.value)} /></label>
        <label>Payment<select value={pay} onChange={(e) => setPay(e.target.value)}>{LEX_PAY.map((p) => <option key={p}>{p}</option>)}</select></label>
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>{LEX_STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label>Vendor<input value={vendor} placeholder="Who was paid?" onChange={(e) => setVendor(e.target.value)} /></label>
      </div>
      <div className="exprollup">
        <div><span>Subtotal</span><b>{money2(sub)}</b></div>
        <div><span>Tax</span><b>{money2(taxN)}</b></div>
        <div className="grand"><span>Grand Total</span><b>{money2(sub + taxN)}</b></div>
      </div>
      <div className="expformacts">
        <button className={"btn slim primary" + (ok ? "" : " soft")} aria-disabled={!ok} onClick={submit}>Log expense</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
        {why && !ok ? <span className="expwhy" role="alert">{why}</span> : null}
      </div>
    </div>
  );
}
